import React from 'react';
import { X, Sliders, DollarSign, PieChart, Calendar, FileText, User, Mail, Phone } from 'lucide-react';
import { Button } from '../ui/Button';
import { format } from 'date-fns';

interface RiskProspect {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  portfolio_value: number;
  risk_score: number;
  asset_allocation: {
    stocks: number;
    bonds: number;
    cash: number;
    other: number;
  };
  submission_date: string;
  lead_status: string;
  lead_source: string;
  assigned_to?: string;
  notes?: string[];
  follow_up_date?: string;
}

interface RiskProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  prospect: RiskProspect;
}

const RiskProfileModal: React.FC<RiskProfileModalProps> = ({ 
  isOpen, 
  onClose, 
  prospect 
}) => {
  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get risk score description
  const getRiskDescription = (score: number) => {
    if (score < 20) return 'Very Conservative';
    if (score < 40) return 'Conservative';
    if (score < 60) return 'Moderate';
    if (score < 80) return 'Aggressive';
    return 'Very Aggressive';
  };

  // Get risk score color
  const getRiskScoreColor = (score: number) => {
    if (score < 30) return 'text-green-600 bg-green-100';
    if (score < 50) return 'text-blue-600 bg-blue-100';
    if (score < 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Risk Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Contact Info */}
          <div className="md:col-span-1">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="ml-3">
                  <h4 className="text-lg font-medium">{prospect.first_name} {prospect.last_name}</h4>
                  <p className="text-sm text-gray-500">
                    {prospect.lead_status.charAt(0).toUpperCase() + prospect.lead_status.slice(1)} Lead
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Email</p>
                    <p className="text-sm text-gray-600">{prospect.email}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Phone className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Phone</p>
                    <p className="text-sm text-gray-600">{prospect.phone}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Submission Date</p>
                    <p className="text-sm text-gray-600">{format(new Date(prospect.submission_date), 'MMM d, yyyy')}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Lead Source</p>
                    <p className="text-sm text-gray-600">{prospect.lead_source}</p>
                  </div>
                </div>
                
                {prospect.assigned_to && (
                  <div className="flex items-start">
                    <User className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Assigned To</p>
                      <p className="text-sm text-gray-600">{prospect.assigned_to}</p>
                    </div>
                  </div>
                )}
                
                {prospect.follow_up_date && (
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 text-gray-400 mt-0.5 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Follow-up Date</p>
                      <p className="text-sm text-gray-600">{format(new Date(prospect.follow_up_date), 'MMM d, yyyy')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Notes Section */}
            {prospect.notes && prospect.notes.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
                <div className="space-y-2">
                  {prospect.notes.map((note, index) => (
                    <div key={index} className="bg-white p-2 rounded border border-gray-200">
                      <p className="text-sm text-gray-600">{note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Right Column - Risk Profile */}
          <div className="md:col-span-2">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="text-lg font-medium mb-4">Risk Profile</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-2">
                    <Sliders className="h-5 w-5 text-primary mr-2" />
                    <h5 className="font-medium">Risk Score</h5>
                  </div>
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-4 mr-2">
                      <div 
                        className="bg-primary h-4 rounded-full" 
                        style={{ width: `${prospect.risk_score}%` }}
                      ></div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskScoreColor(prospect.risk_score)}`}>
                      {prospect.risk_score}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Risk Tolerance: <span className="font-medium">{getRiskDescription(prospect.risk_score)}</span>
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-2">
                    <DollarSign className="h-5 w-5 text-primary mr-2" />
                    <h5 className="font-medium">Portfolio Value</h5>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(prospect.portfolio_value)}
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 mb-4">
                <div className="flex items-center mb-4">
                  <PieChart className="h-5 w-5 text-primary mr-2" />
                  <h5 className="font-medium">Current Asset Allocation</h5>
                </div>
                
                <div className="flex mb-4">
                  <div className="w-1/2">
                    <div className="relative w-32 h-32 mx-auto">
                      {/* Pie chart visualization */}
                      <svg viewBox="0 0 100 100" className="w-full h-full">
                        {/* Stocks slice */}
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="40" 
                          fill="transparent"
                          stroke="#3B82F6" 
                          strokeWidth="20"
                          strokeDasharray={`${prospect.asset_allocation.stocks * 2.51} ${251 - prospect.asset_allocation.stocks * 2.51}`}
                          transform="rotate(-90 50 50)"
                        />
                        {/* Bonds slice */}
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="40" 
                          fill="transparent"
                          stroke="#10B981" 
                          strokeWidth="20"
                          strokeDasharray={`${prospect.asset_allocation.bonds * 2.51} ${251 - prospect.asset_allocation.bonds * 2.51}`}
                          strokeDashoffset={`${-prospect.asset_allocation.stocks * 2.51}`}
                          transform="rotate(-90 50 50)"
                        />
                        {/* Cash slice */}
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="40" 
                          fill="transparent"
                          stroke="#FBBF24" 
                          strokeWidth="20"
                          strokeDasharray={`${prospect.asset_allocation.cash * 2.51} ${251 - prospect.asset_allocation.cash * 2.51}`}
                          strokeDashoffset={`${-(prospect.asset_allocation.stocks + prospect.asset_allocation.bonds) * 2.51}`}
                          transform="rotate(-90 50 50)"
                        />
                        {/* Other slice */}
                        <circle 
                          cx="50" 
                          cy="50" 
                          r="40" 
                          fill="transparent"
                          stroke="#8B5CF6" 
                          strokeWidth="20"
                          strokeDasharray={`${prospect.asset_allocation.other * 2.51} ${251 - prospect.asset_allocation.other * 2.51}`}
                          strokeDashoffset={`${-(prospect.asset_allocation.stocks + prospect.asset_allocation.bonds + prospect.asset_allocation.cash) * 2.51}`}
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="w-1/2">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                          <span className="text-sm">Stocks</span>
                        </div>
                        <span className="text-sm font-medium">{prospect.asset_allocation.stocks}%</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                          <span className="text-sm">Bonds</span>
                        </div>
                        <span className="text-sm font-medium">{prospect.asset_allocation.bonds}%</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-yellow-400 rounded-full mr-2"></div>
                          <span className="text-sm">Cash</span>
                        </div>
                        <span className="text-sm font-medium">{prospect.asset_allocation.cash}%</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                          <span className="text-sm">Other</span>
                        </div>
                        <span className="text-sm font-medium">{prospect.asset_allocation.other}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h5 className="font-medium mb-3">Risk Analysis</h5>
                
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Risk Capacity</span>
                      <span className="text-sm text-gray-600">
                        {prospect.risk_score < 30 ? 'Low' : prospect.risk_score < 70 ? 'Medium' : 'High'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${prospect.risk_score}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Time Horizon</span>
                      <span className="text-sm text-gray-600">
                        {prospect.risk_score < 40 ? 'Short-term' : prospect.risk_score < 70 ? 'Medium-term' : 'Long-term'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${prospect.risk_score}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Investment Knowledge</span>
                      <span className="text-sm text-gray-600">
                        {prospect.risk_score < 30 ? 'Limited' : prospect.risk_score < 60 ? 'Average' : 'Advanced'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${prospect.risk_score}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="font-medium mb-2">Recommended Asset Allocation</h5>
                  
                  <div className="flex items-center mb-2">
                    <div className="w-full bg-gray-200 h-6 rounded-lg overflow-hidden">
                      {/* Recommended allocation based on risk score */}
                      <div className="flex h-full">
                        <div 
                          className="bg-blue-500" 
                          style={{ width: `${Math.min(80, Math.max(20, prospect.risk_score))}%` }}
                          title={`Stocks: ${Math.min(80, Math.max(20, prospect.risk_score))}%`}
                        ></div>
                        <div 
                          className="bg-green-500" 
                          style={{ width: `${Math.min(70, Math.max(10, 100 - prospect.risk_score - 10))}%` }}
                          title={`Bonds: ${Math.min(70, Math.max(10, 100 - prospect.risk_score - 10))}%`}
                        ></div>
                        <div 
                          className="bg-yellow-400" 
                          style={{ width: `${Math.max(5, Math.min(20, 110 - prospect.risk_score))}%` }}
                          title={`Cash: ${Math.max(5, Math.min(20, 110 - prospect.risk_score))}%`}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Stocks: {Math.min(80, Math.max(20, prospect.risk_score))}%</span>
                    <span>Bonds: {Math.min(70, Math.max(10, 100 - prospect.risk_score - 10))}%</span>
                    <span>Cash: {Math.max(5, Math.min(20, 110 - prospect.risk_score))}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end mt-6">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RiskProfileModal;